import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Share2, ShoppingBag, Truck, RotateCcw, ShieldCheck, ChevronRight, Star, Plus, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { ProductCard } from '../components/ProductCard';
import { useScreenInit } from '../useScreenInit.js';
import { useStore } from '../store/useStore';
const giftCardAmounts = [25, 50, 100, 150, 200];
const AccordionItem = ({
  title,
  children,
  isOpen,
  onClick
}: {
  title: string;
  children: React.ReactNode;
  isOpen: boolean;
  onClick: () => void;
}) => <div className="border-b border-gray-100 text-black" data-id="element-840">
    <button onClick={onClick} className="w-full py-3 flex justify-between items-center text-xs uppercase tracking-widest font-bold hover:text-gold transition-colors" data-id="element-841">
      {title}
      {isOpen ? <ChevronUp size={12} data-id="element-842" /> : <ChevronDown size={12} data-id="element-843" />}
    </button>
    <AnimatePresence data-id="element-844">
      {isOpen && <motion.div initial={{
      height: 0,
      opacity: 0
    }} animate={{
      height: 'auto',
      opacity: 1
    }} exit={{
      height: 0,
      opacity: 0
    }} className="overflow-hidden" data-id="element-845">
          <div className="pb-4 text-sm text-gray-600 leading-relaxed" data-id="element-846">
            {children}
          </div>
        </motion.div>}
    </AnimatePresence>
  </div>;
export function ProductDetail() {
  useScreenInit();
  const {
    id
  } = useParams();
  const isGiftCard = id === 'gift-card';
  const {
    addToCart
  } = useStore();
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedAmount, setSelectedAmount] = useState(100);
  const [openAccordion, setOpenAccordion] = useState<string | null>('description');
  const images = isGiftCard ? ['https://cdn.magicpatterns.com/uploads/43ocbeErzL86xDTvHUMoKt/56bc39_3b36d8682b4e40f4bc9e19e8307687eb~mv2.jpg'] : id === '25' ? ['https://cdn.magicpatterns.com/uploads/1NAtNJ2fcku1izttueGKgP/pasted-image.jpg'] : id === '26' ? ['https://cdn.magicpatterns.com/uploads/iMXocAq8RByxdFHyhLJkKz/pasted-image.jpg'] : ['https://images.unsplash.com/photo-1548036328-c9fa89d128fa?q=80&w=2069&auto=format&fit=crop', 'https://images.unsplash.com/photo-1531123897727-8f129e1688ce?q=80&w=1974&auto=format&fit=crop', 'https://images.unsplash.com/photo-1523381210434-271e8be1f52b?q=80&w=2070&auto=format&fit=crop'];
  const relatedProducts = [{
    id: 25,
    name: 'Fila Cap - Gold',
    price: '$60.00',
    image: 'https://cdn.magicpatterns.com/uploads/1NAtNJ2fcku1izttueGKgP/pasted-image.jpg',
    collection: 'TriLyn',
    rating: 5
  }, {
    id: 26,
    name: 'Trilyn Bag Set',
    price: '$100.00',
    image: 'https://cdn.magicpatterns.com/uploads/iMXocAq8RByxdFHyhLJkKz/pasted-image.jpg',
    collection: 'TriLyn',
    rating: 5
  }, {
    id: 1,
    name: 'Heritage Statement Piece',
    price: '$185.00',
    image: images[1],
    collection: 'TriLyn',
    rating: 5
  }, {
    id: 2,
    name: 'Victoria Gold Earrings',
    price: '$85.00',
    image: images[2],
    collection: 'Victoria',
    rating: 5
  }, {
    id: 3,
    name: 'Zarrito Patterned Kaftan',
    price: '$145.00',
    image: images[0],
    collection: 'Zarrito',
    rating: 4
  }, {
    id: 4,
    name: 'Traditional Headwrap',
    price: '$45.00',
    image: images[1],
    collection: 'Heritage',
    rating: 5
  }];
  const allAvailableProducts = [{
    id: '25',
    name: 'Fila Cap - Gold',
    price: '$60.00',
    image: 'https://cdn.magicpatterns.com/uploads/1NAtNJ2fcku1izttueGKgP/pasted-image.jpg',
    collection: 'TriLyn'
  }, {
    id: '26',
    name: 'Trilyn Bag Set',
    price: '$100.00',
    image: 'https://cdn.magicpatterns.com/uploads/iMXocAq8RByxdFHyhLJkKz/pasted-image.jpg',
    collection: 'TriLyn'
  }, {
    id: '1',
    name: 'Heritage Statement Piece',
    price: '$185.00',
    image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?q=80&w=2069&auto=format&fit=crop',
    collection: 'TriLyn'
  }];
  const foundProduct = allAvailableProducts.find(p => p.id === id);
  const currentProduct = foundProduct || {
    id: id || (isGiftCard ? 'gift-card' : '1'),
    name: isGiftCard ? 'TriLyn eGift Card' : 'Heritage Statement Piece',
    price: isGiftCard ? `${selectedAmount}.00` : '$185.00',
    image: isGiftCard ? 'https://cdn.magicpatterns.com/uploads/43ocbeErzL86xDTvHUMoKt/56bc39_3b36d8682b4e40f4bc9e19e8307687eb~mv2.jpg' : images[0],
    collection: isGiftCard ? 'Gift Cards' : 'TriLyn'
  };
  return <div className="min-h-screen bg-white pt-4 pb-10" data-id="element-847">
      <div className="container mx-auto px-4" data-id="element-848">
        {/* Breadcrumbs */}
        <nav className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-400 mb-6" data-id="element-849">
          <Link to="/" className="hover:text-black transition-colors" data-id="element-850">
            Home
          </Link>
          <ChevronRight size={8} data-id="element-851" />
          <Link to="/shop" className="hover:text-black transition-colors" data-id="element-852">
            Shop
          </Link>
          <ChevronRight size={8} data-id="element-853" />
          <span className="text-black font-bold" data-id="element-854">{currentProduct.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-12 items-start" data-id="element-855">
          {/* Image Gallery - Ultra Compact */}
          <div className="lg:col-span-5 flex flex-col md:flex-row gap-3" data-id="element-856">
            {!isGiftCard && images.length > 1 && <div className="order-2 md:order-1 flex md:flex-col gap-2 overflow-x-auto no-scrollbar" data-id="element-857">
                {images.map((img, i) => <button key={i} onClick={() => setSelectedImage(i)} className={`flex-shrink-0 w-12 h-16 border transition-all duration-300 ${selectedImage === i ? 'border-gold' : 'border-transparent hover:border-gray-200'}`} data-id="element-858">
                    <img src={img} className="w-full h-full object-cover" alt={`Gallery ${i}`} data-id="element-859" />
                  </button>)}
              </div>}
            <div className="order-1 md:order-2 flex-1 max-h-[500px] bg-gray-50 relative group overflow-hidden border border-gray-100" data-id="element-860">
              <motion.img key={selectedImage} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.3
            }} src={images[selectedImage]} className="w-full h-full object-cover" alt="Product Main" data-id="element-861" />
              <button className="absolute top-3 right-3 bg-white p-2 rounded-full shadow-md hover:text-gold transition-all duration-300 transform hover:scale-110" data-id="element-862">
                <Heart size={16} data-id="element-863" />
              </button>
            </div>
          </div>

          {/* Product Info - Streamlined */}
          <div className="lg:col-span-7 flex flex-col" data-id="element-864">
            <div className="mb-4" data-id="element-865">
              <div className="flex items-center gap-2 text-gold mb-2" data-id="element-866">
                <div className="flex items-center gap-0.5" data-id="element-867">
                  {[...Array(5)].map((_, i) => <Star key={i} size={10} className="fill-gold text-gold" data-id="element-868" />)}
                </div>
                <button className="text-[11px] font-bold uppercase tracking-widest text-gray-400 hover:text-black transition-colors underline underline-offset-2" data-id="element-869">
                  24 Reviews
                </button>
              </div>
              <h1 className="text-3xl md:text-4xl font-serif mb-4 leading-tight text-black" data-id="element-870">
                {currentProduct.name}
              </h1>
              <div className="flex items-center gap-3" data-id="element-871">
                <p className="text-2xl font-serif text-gold" data-id="element-872">
                  {currentProduct.price}
                </p>
                {!isGiftCard && id !== '25' && id !== '26' && <span className="text-xs text-gray-400 line-through" data-id="element-873">
                    $245.00
                  </span>}
              </div>
            </div>

            <div className="space-y-4 mb-6" data-id="element-874">
              {isGiftCard ? <div className="space-y-4 pt-4 border-t border-gray-100" data-id="element-875">
                  <div data-id="element-876">
                    <label className="block text-[11px] uppercase tracking-widest font-bold mb-3 text-gray-400" data-id="element-877">
                      Select Amount
                    </label>
                    <div className="grid grid-cols-5 gap-2" data-id="element-878">
                      {giftCardAmounts.map(amt => <button key={amt} onClick={() => setSelectedAmount(amt)} className={`py-2 border text-xs font-bold transition-all duration-300 ${selectedAmount === amt ? 'border-black bg-black text-white' : 'border-gray-100 hover:border-gold'}`} data-id="element-879">
                          ${amt}
                        </button>)}
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-880">
                    <input type="email" placeholder="Recipient Email" className="w-full border border-gray-200 px-3 py-2 focus:outline-none focus:border-gold text-sm bg-gray-50/30" data-id="element-881" />
                    <textarea placeholder="Personalized Message (Optional)" rows={2} className="w-full border border-gray-200 px-3 py-2 focus:outline-none focus:border-gold text-sm resize-none bg-gray-50/30" data-id="element-882" />
                  </div>
                </div> : <div className="space-y-4 pt-4 border-t border-gray-100" data-id="element-883">
                  {id !== '25' && id !== '26' && <div data-id="element-884">
                      <div className="flex justify-between items-center mb-3" data-id="element-885">
                        <label className="text-[11px] uppercase tracking-widest font-bold text-gray-400" data-id="element-886">
                          Select Size
                        </label>
                        <button className="text-[11px] uppercase tracking-widest font-bold text-gray-300 hover:text-black transition-colors underline underline-offset-2" data-id="element-887">
                          Size Guide
                        </button>
                      </div>
                      <div className="flex gap-2" data-id="element-888">
                        {['S', 'M', 'L', 'XL'].map(size => <button key={size} onClick={() => setSelectedSize(size)} className={`w-10 h-10 border flex items-center justify-center text-xs font-bold transition-all duration-300 ${selectedSize === size ? 'border-black bg-black text-white' : 'border-gray-100 hover:border-gold'}`} data-id="element-889">
                            {size}
                          </button>)}
                      </div>
                    </div>}
                  <div data-id="element-890">
                    <label className="block text-[11px] uppercase tracking-widest font-bold mb-3 text-gray-400" data-id="element-891">
                      Quantity
                    </label>
                    <div className="flex items-center border border-gray-100 w-28 h-10" data-id="element-892">
                      <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="flex-1 h-full flex items-center justify-center hover:text-gold transition-colors text-black" data-id="element-893">
                        <Minus size={12} data-id="element-894" />
                      </button>
                      <span className="flex-1 text-center font-bold text-sm text-black" data-id="element-895">
                        {quantity}
                      </span>
                      <button onClick={() => setQuantity(quantity + 1)} className="flex-1 h-full flex items-center justify-center hover:text-gold transition-colors text-black" data-id="element-896">
                        <Plus size={12} data-id="element-897" />
                      </button>
                    </div>
                  </div>
                </div>}

              <div className="flex flex-col gap-2 pt-2" data-id="element-898">
                <button onClick={() => addToCart(currentProduct, isGiftCard || id === '25' || id === '26' ? undefined : selectedSize)} className="w-full bg-black text-white py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-gold transition-all duration-500 flex items-center justify-center gap-2 shadow-md" data-id="element-899">
                  <ShoppingBag size={14} data-id="element-900" /> Add to Bag
                </button>
                <button className="w-full border border-black py-3 text-xs uppercase tracking-[0.2em] font-bold hover:bg-black hover:text-white transition-all duration-500 text-black" data-id="element-901">
                  Buy It Now
                </button>
              </div>
            </div>

            {/* Accordion Section - Ultra Streamlined */}
            <div className="border-t border-gray-100" data-id="element-902">
              <AccordionItem title="Product Description" isOpen={openAccordion === 'description'} onClick={() => setOpenAccordion(openAccordion === 'description' ? null : 'description')} data-id="element-903">
                <p data-id="element-904">
                  {id === '25' ? 'A stunning gold Fila cap, handcrafted with traditional Aso Oke fabric. A perfect statement piece for cultural celebrations and modern high-fashion looks.' : id === '26' ? 'The ultimate luxury travel companion. This premium bag set features a spacious duffle, a matching backpack, and a compact toiletry pouch, all finished in a sophisticated woven texture.' : 'A masterpiece of craftsmanship, this statement piece combines traditional African patterns with a modern silhouette.'}
                </p>
              </AccordionItem>

              <AccordionItem title="Shipping & Returns" isOpen={openAccordion === 'shipping'} onClick={() => setOpenAccordion(openAccordion === 'shipping' ? null : 'shipping')} data-id="element-905">
                <div className="space-y-2" data-id="element-906">
                  <div className="flex items-start gap-2" data-id="element-907">
                    <Truck size={14} className="text-gold flex-shrink-0" data-id="element-908" />
                    <p data-id="element-909">Free standard shipping over $150.</p>
                  </div>
                  <div className="flex items-start gap-2" data-id="element-910">
                    <RotateCcw size={14} className="text-gold flex-shrink-0" data-id="element-911" />
                    <p data-id="element-912">Easy 30-day returns.</p>
                  </div>
                </div>
              </AccordionItem>
            </div>

            <div className="pt-4 flex items-center justify-between border-t border-gray-100 mt-4" data-id="element-913">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest font-bold text-gray-300" data-id="element-914">
                <ShieldCheck size={12} className="text-gold" data-id="element-915" />
                <span data-id="element-916">Secure Payment</span>
              </div>
              <div className="flex items-center gap-3" data-id="element-917">
                <span className="text-[10px] uppercase tracking-widest font-bold text-gray-300" data-id="element-918">
                  Share:
                </span>
                <button className="text-gray-400 hover:text-gold transition-colors" data-id="element-919">
                  <Share2 size={12} data-id="element-920" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Related Products - Compact Grid */}
        <section className="mt-16" data-id="element-921">
          <div className="text-center mb-8" data-id="element-922">
            <span className="text-gold uppercase tracking-[0.3em] text-[10px] font-bold block mb-2" data-id="element-923">
              Complete the Look
            </span>
            <h2 className="text-xl font-serif mb-3 text-black" data-id="element-924">
              You May Also Like
            </h2>
            <div className="w-12 h-0.5 bg-gold mx-auto" data-id="element-925"></div>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-926">
            {relatedProducts.map(product => <ProductCard key={product.id} product={product as any} data-id="element-927" />)}
          </div>
        </section>
      </div>
    </div>;
}